import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Input, Label, Textarea,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui';
import { ApiError } from '@/lib/api/client';
import type {
  IqServiceDetail,
  CreateIqServiceRequest,
  UpdateIqServiceRequest,
  GatewayMode,
  IqServiceStatus,
} from '@/types/iq-service';

interface IqServiceFormProps {
  mode: 'create' | 'edit';
  initialData?: IqServiceDetail;
  onSubmit: (data: CreateIqServiceRequest | UpdateIqServiceRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
  error?: Error | null;
}

const MERCHANT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{2,48}[a-z0-9]$/;

export function IqServiceForm({ mode, initialData, onSubmit, onCancel, isLoading = false, error }: IqServiceFormProps) {
  const { t } = useTranslation('iq-services');
  const isEditMode = mode === 'edit';

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [gatewayBaseUrl, setGatewayBaseUrl] = useState('');
  const [gatewayEndpoint, setGatewayEndpoint] = useState('/mobile2-payment/global');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [gatewayMode, setGatewayMode] = useState<GatewayMode>('live');
  const [status, setStatus] = useState<IqServiceStatus>('active');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setDisplayName(initialData.displayName);
      setDescription(initialData.description ?? '');
      setGatewayBaseUrl(initialData.gatewayBaseUrl);
      setGatewayEndpoint(initialData.gatewayEndpoint ?? '/mobile2-payment/global');
      setUsername(initialData.credentials.username);
      setPassword(''); // never pre-fill password (shown as ****)
      setMerchantName(initialData.merchantName);
      setMerchantId(initialData.merchantId);
      setGatewayMode(initialData.gatewayMode);
      setStatus(initialData.status);
    }
  }, [initialData]);

  // Map API errors to field-level errors
  useEffect(() => {
    if (error instanceof ApiError) {
      const fieldErrors: Record<string, string> = {};
      if (error.code === 'VALIDATION_ERROR' && error.details) {
        for (const detail of error.details) {
          fieldErrors[detail.field] = detail.message;
        }
      }
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
    }
  }, [error]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!displayName.trim()) {
      newErrors.displayName = t('form.validation.displayNameRequired');
    } else if (displayName.length > 200) {
      newErrors.displayName = t('form.validation.displayNameMaxLength');
    }

    if (!gatewayBaseUrl.trim()) {
      newErrors.gatewayBaseUrl = t('form.validation.gatewayBaseUrlRequired');
    } else if (!gatewayBaseUrl.startsWith('https://')) {
      newErrors.gatewayBaseUrl = t('form.validation.gatewayBaseUrlHttps');
    }

    if (!username.trim()) {
      newErrors.username = t('form.validation.usernameRequired');
    } else if (username.length > 50) {
      newErrors.username = t('form.validation.usernameMaxLength');
    }

    // Password required only in create mode
    if (!isEditMode && !password) {
      newErrors.password = t('form.validation.passwordRequired');
    }
    if (password && password.length > 200) {
      newErrors.password = t('form.validation.passwordMaxLength');
    }

    if (!merchantName.trim()) {
      newErrors.merchantName = t('form.validation.merchantNameRequired');
    } else if (merchantName.length > 200) {
      newErrors.merchantName = t('form.validation.merchantNameMaxLength');
    }

    if (!merchantId.trim()) {
      newErrors.merchantId = t('form.validation.merchantIdRequired');
    } else if (!MERCHANT_ID_PATTERN.test(merchantId)) {
      newErrors.merchantId = t('form.validation.merchantIdPattern');
    }

    if (description.length > 500) {
      newErrors.description = t('form.validation.descriptionMaxLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (isEditMode && initialData) {
      const data: UpdateIqServiceRequest = {
        updatedAt: initialData.updatedAt,
        displayName,
        description: description || undefined,
        gatewayBaseUrl,
        gatewayEndpoint: gatewayEndpoint || undefined,
        username,
        merchantName,
        merchantId,
        gatewayMode,
        status,
      };
      // Only include password if user actually typed one
      if (password) {
        data.password = password;
      }
      onSubmit(data);
    } else {
      const data: CreateIqServiceRequest = {
        displayName,
        gatewayBaseUrl,
        username,
        password,
        merchantName,
        merchantId,
        gatewayEndpoint: gatewayEndpoint || undefined,
        gatewayMode,
        status,
        description: description || undefined,
      };
      onSubmit(data);
    }
  };

  const clearFieldError = (field: string) => {
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {/* displayName */}
        <div className="space-y-2">
          <Label htmlFor="displayName">
            {t('form.label.displayName')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="displayName"
            placeholder={t('form.placeholder.displayName')}
            value={displayName}
            onChange={(e) => { setDisplayName(e.target.value); clearFieldError('displayName'); }}
            disabled={isLoading}
            className={errors.displayName ? 'border-destructive' : ''}
          />
          {errors.displayName && <p className="text-[13px] text-destructive">{errors.displayName}</p>}
        </div>

        {/* gatewayBaseUrl */}
        <div className="space-y-2">
          <Label htmlFor="gatewayBaseUrl">
            {t('form.label.gatewayBaseUrl')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="gatewayBaseUrl"
            type="url"
            placeholder={t('form.placeholder.gatewayBaseUrl')}
            value={gatewayBaseUrl}
            onChange={(e) => { setGatewayBaseUrl(e.target.value); clearFieldError('gatewayBaseUrl'); }}
            disabled={isLoading}
            className={errors.gatewayBaseUrl ? 'border-destructive' : ''}
          />
          {errors.gatewayBaseUrl && <p className="text-[13px] text-destructive">{errors.gatewayBaseUrl}</p>}
        </div>

        {/* gatewayEndpoint */}
        <div className="space-y-2">
          <Label htmlFor="gatewayEndpoint">{t('form.label.gatewayEndpoint')}</Label>
          <Input
            id="gatewayEndpoint"
            placeholder={t('form.placeholder.gatewayEndpoint')}
            value={gatewayEndpoint}
            onChange={(e) => setGatewayEndpoint(e.target.value)}
            disabled={isLoading}
          />
          <p className="text-xs text-muted-foreground">{t('form.hint.gatewayEndpoint')}</p>
        </div>

        {/* username */}
        <div className="space-y-2">
          <Label htmlFor="username">
            {t('form.label.username')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="username"
            placeholder={t('form.placeholder.username')}
            value={username}
            onChange={(e) => { setUsername(e.target.value); clearFieldError('username'); }}
            disabled={isLoading}
            className={errors.username ? 'border-destructive' : ''}
          />
          {errors.username && <p className="text-[13px] text-destructive">{errors.username}</p>}
        </div>

        {/* password */}
        <div className="space-y-2">
          <Label htmlFor="password">
            {t('form.label.password')} {!isEditMode && <span className="text-destructive">*</span>}
          </Label>
          <Input
            id="password"
            type="password"
            placeholder={isEditMode ? '****' : t('form.placeholder.password')}
            value={password}
            onChange={(e) => { setPassword(e.target.value); clearFieldError('password'); }}
            disabled={isLoading}
            className={errors.password ? 'border-destructive' : ''}
          />
          <p className="text-xs text-muted-foreground">
            {isEditMode ? t('form.hint.passwordChange') : t('form.hint.password')}
          </p>
          {errors.password && <p className="text-[13px] text-destructive">{errors.password}</p>}
        </div>

        {/* merchantName */}
        <div className="space-y-2">
          <Label htmlFor="merchantName">
            {t('form.label.merchantName')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="merchantName"
            placeholder={t('form.placeholder.merchantName')}
            value={merchantName}
            onChange={(e) => { setMerchantName(e.target.value); clearFieldError('merchantName'); }}
            disabled={isLoading}
            className={errors.merchantName ? 'border-destructive' : ''}
          />
          {errors.merchantName && <p className="text-[13px] text-destructive">{errors.merchantName}</p>}
        </div>

        {/* merchantId */}
        <div className="space-y-2">
          <Label htmlFor="merchantId">
            {t('form.label.merchantId')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="merchantId"
            placeholder={t('form.placeholder.merchantId')}
            value={merchantId}
            onChange={(e) => { setMerchantId(e.target.value); clearFieldError('merchantId'); }}
            disabled={isLoading}
            className={errors.merchantId ? 'border-destructive' : ''}
          />
          <p className="text-xs text-muted-foreground">{t('form.hint.merchantId')}</p>
          {errors.merchantId && <p className="text-[13px] text-destructive">{errors.merchantId}</p>}
        </div>

        {/* gatewayMode + status side by side */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="gatewayMode">{t('form.label.gatewayMode')}</Label>
            <Select value={gatewayMode} onValueChange={(v) => setGatewayMode(v as GatewayMode)} disabled={isLoading}>
              <SelectTrigger id="gatewayMode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="live">{t('gatewayMode.live')}</SelectItem>
                <SelectItem value="mock">{t('gatewayMode.mock')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">{t('form.label.status')}</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as IqServiceStatus)} disabled={isLoading}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t('status.active')}</SelectItem>
                <SelectItem value="inactive">{t('status.inactive')}</SelectItem>
                <SelectItem value="suspended">{t('status.suspended')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* description */}
        <div className="space-y-2">
          <Label htmlFor="description">{t('form.label.description')}</Label>
          <Textarea
            id="description"
            placeholder={t('form.placeholder.description')}
            value={description}
            onChange={(e) => { setDescription(e.target.value); clearFieldError('description'); }}
            disabled={isLoading}
            rows={3}
            className={errors.description ? 'border-destructive' : ''}
          />
          {errors.description && <p className="text-[13px] text-destructive">{errors.description}</p>}
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          {t('form.cancel')}
        </Button>
        <Button type="submit" loading={isLoading}>
          {isEditMode ? t('form.submit') : t('form.create')}
        </Button>
      </div>
    </form>
  );
}
